import { i18n, initTranslation } from "../modules/i18n";
import initPage from "../modules/page";
import { initCarousels } from "../modules/product-collection";
import {
  sendClickEvent,
  sendRemoveFromCartEvent,
  sendViewCartEvent,
} from "../modules/analytics/google-analytics";
import {
  addItemToCartRequest,
  removeItemFromCartRequest,
  updateCartItemQuantityRequest,
} from "../modules/api";
import { initPayPalMessaging } from "../modules/paypal-messaging.js";
import "../../scss/cart.scss";
import { Logger, select } from "@fruugo/utilities";
import { toggleClass } from "../modules/utilities";

const Log = new Logger(window.environment);

const errors: string[] = [];

const { shippingGroups } = window;

function cartLoading(rowElement: Element) {
  const loader = document.createElement("img");
  loader.setAttribute("src", "/marketplace/images/loader.svg");
  loader.setAttribute("class", "loader-svg");
  loader.setAttribute("alt", "loading");
  rowElement.classList.add("Cart__Item--Loading");
  rowElement.append(loader);
}

function stopCartLoading(rowElement: Element) {
  rowElement.classList.remove("Cart__Item--Loading");
  rowElement.querySelector(".loader-svg")?.remove();
}

function changeProductQuantity(quantityField: Element, value: string) {
  const rowElement = quantityField.closest(".Cart__Item");
  const form = quantityField.closest(".form");

  if (!rowElement || !form) return;

  const originalQualityField =
    form?.querySelector<HTMLInputElement>(".js-quantity-field");
  if (!originalQualityField) return;

  const originalQuantity = originalQualityField
    ? originalQualityField.value
    : null;

  if (!originalQuantity) return;

  const newQuantity =
    value === "increase"
      ? parseInt(originalQuantity) + 1
      : parseInt(originalQuantity) - 1;

  const skuIdInput = form?.querySelector<HTMLInputElement>("input[name=skuId]");
  if (!skuIdInput) return;

  const skuId = skuIdInput.value;

  cartLoading(rowElement);

  if (newQuantity <= 0) {
    removeItemFromCartRequest(skuId, originalQuantity)
      .then((data) => {
        if (data.success === true) {
          clearErrors(rowElement);
          location.reload();
        } else {
          addError(data.message, rowElement);
        }
      })
      .catch((error) => {
        addError(i18n("cart.generic.error"), rowElement);
        Log.error(error);
      })
      .finally(() => stopCartLoading(rowElement));
  } else {
    updateCartItemQuantityRequest(skuId, newQuantity)
      .then((data) => {
        if (data.success === true) {
          originalQualityField.value = newQuantity.toString();
          clearErrors(rowElement);
          location.reload();
        } else {
          addError(data.message, rowElement);
        }
      })
      .catch((error) => {
        addError(error.message, rowElement);
        Log.error(error);
      })
      .finally(() => stopCartLoading(rowElement));
  }
}

const addError = (message?: string, element?: Element) => {
  if (!element) {
    // if page level error
    const errorElement = document.querySelector(".js-cart-error");
    if (!errorElement) return;

    errorElement.classList.remove("d-none");
    if (message) {
      const alert = errorElement.querySelector(".alert");
      if (!alert) return;
      alert.innerHTML = message;
    }
  } else {
    // if cart item level error
    if (!errors.some((error) => error === message)) {
      message && errors.push(message);
    }

    const errorElement = element.querySelector(".js-cart-item-error");
    if (errorElement) {
      const alert = errorElement.querySelector(".alert");
      if (alert) {
        errorElement.classList.remove("d-none");
        alert.innerHTML = errors.reduce(
          (prev, current) => `${prev} <br/> ${current}`
        );
      }
    }
  }
};

const clearErrors = (element?: Element) => {
  if (!element) {
    // clear page level errors
    const errorElement = document.querySelector(".js-cart-error");
    if (errorElement) {
      errorElement.classList.add("d-none");
      const alert = errorElement.querySelector(".alert");
      if (alert) {
        alert.innerHTML = i18n("cart.generic.error");
      }
    }
  } else {
    // clear cart level item errors
    const errorElement = element.querySelector(".js-cart-item-error");
    if (errorElement) {
      const alert = errorElement.querySelector(".alert");
      if (alert) {
        errorElement.classList.add("d-none");
        alert.innerHTML = "";
      }
    }
  }
};

function getSkuFromShippingGroup(skuId: number) {
  return shippingGroups
    ?.flatMap((group) => group.cartItems)
    ?.find((item) => item.skuId == skuId)?.sku;
}

function initPageSpecificAnalytics() {
  if (!shippingGroups) return;

  const cartItems = shippingGroups.flatMap((group) => group.cartItems);
  if (cartItems.length > 0) sendViewCartEvent(cartItems);
}

function initCartSummary() {
  const button = select<HTMLButtonElement>(".js-toggle-cart-fee-details");
  if (!button) return;

  button.addEventListener("click", toggleCartSummary);

  function toggleCartSummary() {
    const summary = select<HTMLElement>(".js-cart-fee-details");
    if (!summary) return;

    const icon = button?.querySelector(".icon");
    icon && toggleClass(icon, "tw:rotate-180");

    toggleClass(summary, "tw:h-0");
    toggleClass(summary, "tw:h-auto");

    sendClickEvent("cart_fees_detail_toggle", "Cart Fee Toggle");
  }
}

async function initCartPage() {
  await initTranslation();
  initPage();
  initCarousels();
  initPayPalMessaging();
  initPageSpecificAnalytics();
  initCartSummary();

  const quantityFields =
    document.querySelectorAll<HTMLInputElement>(".js-quantity-field");
  if (quantityFields) {
    quantityFields.forEach((quantityField) => {
      quantityField.addEventListener("change", () => {
        const rowElement = quantityField.closest(".Cart__Item");
        const form = quantityField.closest(".form");
        const skuIdInput =
          form?.querySelector<HTMLInputElement>("input[name=skuId]");
        const skuId = skuIdInput?.value;
        const quantity = parseInt(quantityField.value);

        if (!form || !skuId) return;

        const originalQuantityInput = form.querySelector<HTMLInputElement>(
          "input[name=originalQty]"
        );

        if (!originalQuantityInput) return;

        const originalQuantity = originalQuantityInput.value;

        if (!rowElement) return;

        cartLoading(rowElement);

        if (quantity <= 0) {
          removeItemFromCartRequest(skuId, originalQuantity)
            .then((data) => {
              if (data.success === true) {
                clearErrors(rowElement);
                location.reload();
              } else {
                addError(data.message, rowElement);
              }
            })
            .catch((error) => {
              addError(i18n("cart.generic.error"), rowElement);
              Log.error(error);
            })
            .finally(() => stopCartLoading(rowElement));
        } else {
          updateCartItemQuantityRequest(skuId, quantity)
            .then((data) => {
              if (data.success === true) {
                clearErrors(rowElement);
                location.reload();
              } else {
                const quantityField =
                  form.querySelector<HTMLInputElement>(".js-quantity-field");
                if (!quantityField) return;
                quantityField.value = originalQuantity;
                addError(data.error, rowElement);
              }
            })
            .catch((error) => {
              addError(error.message, rowElement);
              Log.error(error);
            })
            .finally(() => stopCartLoading(rowElement));
        }
      });
    });
  }

  const increaseQuantityFields = document.querySelectorAll(
    ".js-increase-quantity"
  );
  if (increaseQuantityFields) {
    increaseQuantityFields.forEach((increaseQuantityField) => {
      increaseQuantityField.addEventListener("click", (event) => {
        event.preventDefault();
        changeProductQuantity(increaseQuantityField, "increase");
      });
    });
  }

  const decreaseQuantityFields = document.querySelectorAll(
    ".js-decrease-quantity"
  );
  if (decreaseQuantityFields) {
    decreaseQuantityFields.forEach((decreaseQuantityField) => {
      decreaseQuantityField.addEventListener("click", (event) => {
        event.preventDefault();
        changeProductQuantity(decreaseQuantityField, "decrease");
      });
    });
  }

  const removeItemButtons =
    document.querySelectorAll<HTMLElement>(".js-remove-item");
  if (removeItemButtons) {
    removeItemButtons.forEach((removeItemButton) => {
      removeItemButton.addEventListener("click", (event) => {
        event.preventDefault();
        const rowElement = removeItemButton.closest(".Cart__Item");
        if (!rowElement) return;

        const { skuId, qty } = removeItemButton.dataset;
        if (!skuId || !qty) return;

        cartLoading(rowElement);

        removeItemFromCartRequest(skuId, qty)
          .then((data) => {
            if (data.success === true) {
              clearErrors(rowElement);

              const sku = getSkuFromShippingGroup(parseInt(skuId));
              sku && sendRemoveFromCartEvent(sku, parseInt(qty));

              setTimeout(() => location.reload(), 50);
            } else {
              addError(data.message, rowElement);
            }
          })
          .catch((error) => {
            addError(i18n("cart.generic.error"), rowElement);
            Log.error(error);
          })
          .finally(() => stopCartLoading(rowElement));
      });
    });
  }

  const restoreProductButtons = document.querySelectorAll<HTMLInputElement>(
    ".js-restore-product"
  );
  if (restoreProductButtons) {
    restoreProductButtons.forEach((restoreProductButton) => {
      restoreProductButton.addEventListener("click", () => {
        const { skuId, qty } = restoreProductButton.dataset;
        if (!skuId || !qty) return;

        addItemToCartRequest(skuId, parseInt(qty))
          .then((data) => {
            if (data.success === true) {
              clearErrors();
              location.reload();
            } else {
              addError(data.message);
            }
          })
          .catch((error) => {
            addError();
            Log.error(error);
          });
      });
    });
  }
}

initCartPage();
